import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { 
    Users, Calendar, CreditCard, Save, CheckCircle, AlertCircle, Plus, Info, Search, Edit2, 
    Coins, Banknote, ListCollapse, UserCheck
} from 'lucide-react';
import Loading from '../../components/Loading';

export default function PayrollManagement() {
    const toast = useToast();
    const [activeTab, setActiveTab] = useState('roster'); // 'roster', 'attendance', 'payroll'
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    
    // Roster edit state
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [salaryForm, setSalaryForm] = useState({
        base_salary: 0,
        commission_per_delivery: 0,
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        is_active: true
    });

    // Attendance state
    const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
    const [attendanceLogs, setAttendanceLogs] = useState({}); // { employee_id: { status, notes } }
    const [submittingAttendance, setSubmittingAttendance] = useState(false);

    // Payroll Calculation state
    const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
    const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
    const [payrollSlips, setPayrollSlips] = useState([]);
    const [calculatingPayroll, setCalculatingPayroll] = useState(false);
    const [submittingSlipId, setSubmittingSlipId] = useState(null);

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/payroll/employees');
            if (res.data.success) {
                setEmployees(res.data.employees);
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
            toast.error('Failed to load employee profiles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    // Load attendance for the chosen date
    const loadAttendanceForDate = async () => {
        try {
            setSubmittingAttendance(true);
            const dateObj = new Date(attendanceDate);
            const month = dateObj.getMonth() + 1;
            const year = dateObj.getFullYear();
            const res = await api.get(`/api/payroll/attendance?month=${month}&year=${year}`);
            
            if (res.data.success) {
                const logsForDate = {};
                res.data.logs.forEach(log => {
                    if (log.date === attendanceDate) {
                        logsForDate[log.employee_id] = { status: log.status, notes: log.notes || '' };
                    }
                });
                
                // Initialize default 'present' for employees with no logs for the day
                const initialLogs = {};
                res.data.employees.forEach(emp => {
                    initialLogs[emp.employee_id] = logsForDate[emp.employee_id] || { status: 'present', notes: '' };
                });
                setAttendanceLogs(initialLogs);
            }
        } catch (err) {
            console.error('Error loading attendance logs:', err);
            toast.error('Failed to load attendance logs');
        } finally {
            setSubmittingAttendance(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'attendance' && attendanceDate) {
            loadAttendanceForDate();
        }
    }, [activeTab, attendanceDate]);

    // Fetch payroll calculations
    const loadPayrollCalculation = async () => {
        try {
            setCalculatingPayroll(true);
            const res = await api.get(`/api/payroll/calculate?month=${payrollMonth}&year=${payrollYear}`);
            if (res.data.success) {
                setPayrollSlips(res.data.payroll);
            }
        } catch (err) {
            console.error('Error calculating payroll:', err);
            toast.error('Failed to load payroll calculations');
        } finally {
            setCalculatingPayroll(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'payroll') {
            loadPayrollCalculation();
        }
    }, [activeTab, payrollMonth, payrollYear]);

    // Handle Edit Roster Setup
    const handleStartEdit = (emp) => {
        setEditingEmployee(emp);
        setSalaryForm({
            base_salary: emp.base_salary || 0,
            commission_per_delivery: emp.commission_per_delivery || 0,
            bank_name: emp.bank_name || '',
            account_number: emp.account_number || '',
            ifsc_code: emp.ifsc_code || '',
            is_active: emp.is_active !== false
        });
    };

    // Save Roster details
    const handleSaveRoster = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                user_id: editingEmployee.user_id,
                ...salaryForm
            };
            const res = await api.post('/api/payroll/employees', payload);
            if (res.data.success) {
                toast.success('Roster details updated successfully');
                setEditingEmployee(null);
                fetchEmployees();
            }
        } catch (err) {
            console.error('Error saving settings:', err);
            toast.error('Failed to update employee details');
        }
    };

    // Handle attendance changes locally
    const handleAttendanceChange = (employee_id, field, value) => {
        setAttendanceLogs(prev => ({
            ...prev,
            [employee_id]: {
                ...prev[employee_id],
                [field]: value
            }
        }));
    };

    // Save Attendance logs
    const handleSaveAttendance = async () => {
        try {
            setSubmittingAttendance(true);
            const entries = Object.keys(attendanceLogs).map(empId => ({
                employee_id: parseInt(empId),
                date: attendanceDate,
                status: attendanceLogs[empId].status,
                notes: attendanceLogs[empId].notes
            }));

            const res = await api.post('/api/payroll/attendance', { entries });
            if (res.data.success) {
                toast.success('Attendance records saved successfully');
            }
        } catch (err) {
            console.error('Error saving attendance:', err);
            toast.error('Failed to update attendance logs');
        } finally {
            setSubmittingAttendance(false);
        }
    };

    // Handle dynamic updates in the calculated list
    const handlePayrollSlipChange = (index, field, value) => {
        const updated = [...payrollSlips];
        const val = parseFloat(value) || 0;
        updated[index][field] = val;

        // Recalculate net salary dynamically: base_salary - deductions + commission + bonus
        const base = parseFloat(updated[index].base_salary) || 0;
        const totalComm = parseFloat(updated[index].total_commission) || 0;
        const bonus = field === 'bonus' ? val : (parseFloat(updated[index].bonus) || 0);
        const ded = field === 'deductions' ? val : (parseFloat(updated[index].deductions) || 0);
        
        updated[index].net_salary = Number(Math.max(0, base - ded + totalComm + bonus).toFixed(2));
        setPayrollSlips(updated);
    };

    // Handle text references updates
    const handlePayrollTextChange = (index, field, value) => {
        const updated = [...payrollSlips];
        updated[index][field] = value;
        setPayrollSlips(updated);
    };

    // Submit individual payroll slip record
    const handleSavePayrollSlip = async (index, status) => {
        const slip = payrollSlips[index];
        try {
            setSubmittingSlipId(slip.employee_id);
            const payload = {
                employee_id: slip.employee_id,
                month: parseInt(payrollMonth),
                year: parseInt(payrollYear),
                base_salary: slip.base_salary,
                total_deliveries: slip.total_deliveries,
                total_commission: slip.total_commission,
                bonus: slip.bonus,
                unpaid_leaves: slip.unpaid_leaves,
                deductions: slip.deductions,
                net_salary: slip.net_salary,
                payment_status: status,
                payment_reference: slip.payment_reference
            };

            const endpoint = status === 'paid' ? '/api/payroll/disburse' : '/api/payroll/save-slip';
            const res = await api.post(endpoint, payload);
            if (res.data.success) {
                toast.success(status === 'paid' ? `Payout processed! UTR: ${res.data.record?.payment_reference || ''}` : 'Payslip draft saved');
                loadPayrollCalculation();
            }
        } catch (err) {
            console.error('Error saving payslip:', err);
            toast.error(err.response?.data?.message || 'Failed to process payslip record');
        } finally {
            setSubmittingSlipId(null);
        }
    };

    if (loading) {
        return <Loading mode="full" message="Loading payroll systems..." />;
    }

    return (
        <div className="container mx-auto p-4 max-w-6xl space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between pb-5 border-b border-slate-200 gap-4">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                        <Coins className="w-7 h-7 text-primary-600" /> Employee Payroll & Attendance
                    </h1>
                    <p className="text-slate-500 text-xs mt-1">Configure compensation rates, record manual attendance registry, and process monthly direct-bank pay slips.</p>
                </div>

                {/* Tabs */}
                <div className="flex bg-slate-100 p-1 rounded-xl">
                    <button
                        onClick={() => setActiveTab('roster')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                            activeTab === 'roster' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                        }`}
                    >
                        <Users className="w-4 h-4" /> Staff Setup
                    </button>
                    <button
                        onClick={() => setActiveTab('attendance')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                            activeTab === 'attendance' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                        }`}
                    >
                        <Calendar className="w-4 h-4" /> Attendance Register
                    </button>
                    <button
                        onClick={() => setActiveTab('payroll')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                            activeTab === 'payroll' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                        }`}
                    >
                        <CreditCard className="w-4 h-4" /> Salary Calculations
                    </button>
                </div>
            </div>

            {/* TAB 1: STAFF ROSTER SETUP */}
            {activeTab === 'roster' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* List */}
                    <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <h3 className="font-black text-slate-900 text-sm">Restaurant Roster & Pay Structures</h3>
                            <span className="bg-primary-50 text-primary-700 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
                                {employees.length} Staff Members
                            </span>
                        </div>
                        <div className="divide-y divide-slate-100 overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[500px]">
                                <thead>
                                    <tr className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100">
                                        <th className="p-4">Name / Contact</th>
                                        <th className="p-4">Role</th>
                                        <th className="p-4">Compensation Structure</th>
                                        <th className="p-4">Bank Details</th>
                                        <th className="p-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {employees.map((emp) => (
                                        <tr key={emp.user_id} className={`hover:bg-slate-50/50 transition-colors ${!emp.is_active ? 'opacity-60 bg-slate-50/30' : ''}`}>
                                            <td className="p-4">
                                                <div className="font-bold text-slate-900 text-xs">{emp.full_name}</div>
                                                <div className="text-[10px] text-slate-400 mt-0.5">{emp.email} | {emp.phone || 'No phone'}</div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                                                    emp.role === 'delivery_partner' ? 'bg-indigo-50 text-indigo-700' :
                                                    emp.role === 'kitchen_staff' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                                                }`}>
                                                    {emp.role === 'delivery_partner' ? 'Delivery' : emp.role === 'kitchen_staff' ? 'Kitchen' : 'Admin'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-xs font-bold text-slate-800">₹{emp.base_salary}/month</div>
                                                {emp.role === 'delivery_partner' && (
                                                    <div className="text-[10px] text-emerald-600 font-bold mt-0.5">+ ₹{emp.commission_per_delivery} per delivery</div>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                {emp.account_number ? (
                                                    <div className="text-[10px] text-slate-600 font-bold">
                                                        <div>{emp.bank_name}</div>
                                                        <div className="font-mono text-slate-400 mt-0.5">{emp.account_number}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-slate-300 font-bold">Not configured</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right">
                                                <button
                                                    onClick={() => handleStartEdit(emp)}
                                                    className="p-2 text-slate-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                                                    title="Edit Payroll Profiles"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Edit Form Card */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden p-6 self-start">
                        {editingEmployee ? (
                            <form onSubmit={handleSaveRoster} className="space-y-4">
                                <h3 className="font-black text-slate-900 text-sm pb-3 border-b border-slate-100 flex items-center justify-between">
                                    <span>Configure Salaries</span>
                                    <span className="text-xs text-primary-600 font-bold">{editingEmployee.full_name}</span>
                                </h3>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Base Salary (Monthly Base)</label>
                                    <input
                                        type="number"
                                        value={salaryForm.base_salary}
                                        onChange={(e) => setSalaryForm({ ...salaryForm, base_salary: parseFloat(e.target.value) || 0 })}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                        placeholder="e.g. 15000"
                                        min="0"
                                        required
                                    />
                                </div>

                                {editingEmployee.role === 'delivery_partner' && (
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Commission per Order Payout</label>
                                        <input
                                            type="number"
                                            value={salaryForm.commission_per_delivery}
                                            onChange={(e) => setSalaryForm({ ...salaryForm, commission_per_delivery: parseFloat(e.target.value) || 0 })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-bold"
                                            placeholder="e.g. 30"
                                            min="0"
                                        />
                                    </div>
                                )}

                                <div className="pt-2 border-t border-slate-100 space-y-3">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Direct Bank Details (For manual transfers)</h4>
                                    
                                    <div>
                                        <input
                                            type="text"
                                            value={salaryForm.bank_name}
                                            onChange={(e) => setSalaryForm({ ...salaryForm, bank_name: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold"
                                            placeholder="Bank Name (e.g. HDFC)"
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            value={salaryForm.account_number}
                                            onChange={(e) => setSalaryForm({ ...salaryForm, account_number: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold font-mono"
                                            placeholder="Account Number"
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            value={salaryForm.ifsc_code}
                                            onChange={(e) => setSalaryForm({ ...salaryForm, ifsc_code: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold font-mono uppercase"
                                            placeholder="IFSC Code"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 py-2">
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        checked={salaryForm.is_active}
                                        onChange={(e) => setSalaryForm({ ...salaryForm, is_active: e.target.checked })}
                                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500 w-4 h-4"
                                    />
                                    <label htmlFor="is_active" className="text-xs font-bold text-slate-700">Active on payroll calculations</label>
                                </div>

                                <div className="flex gap-2 pt-2">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm transition-all"
                                    >
                                        <Save className="w-4 h-4" /> Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditingEmployee(null)}
                                        className="bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold py-2.5 px-4 rounded-xl text-xs transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="text-center py-12 text-slate-400">
                                <Info className="w-10 h-10 mx-auto mb-3 opacity-30 text-slate-900" />
                                <p className="text-xs font-bold">Select an employee from the roster list to configure their salary rates and bank details.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB 2: ATTENDANCE REGISTER */}
            {activeTab === 'attendance' && (
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-6">
                    {/* Control Panel */}
                    <div className="flex flex-col sm:flex-row items-center justify-between border-b border-slate-100 pb-4 gap-4">
                        <div className="flex items-center gap-3">
                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Select Attendance Date:</label>
                            <input
                                type="date"
                                value={attendanceDate}
                                onChange={(e) => setAttendanceDate(e.target.value)}
                                className="bg-slate-50 border border-slate-200 rounded-xl p-2 px-3 text-xs font-bold"
                            />
                        </div>
                        <button
                            onClick={handleSaveAttendance}
                            disabled={submittingAttendance || Object.keys(attendanceLogs).length === 0}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 shadow-sm transition-all disabled:opacity-50"
                        >
                            <CheckCircle className="w-4 h-4" /> Save Daily Register
                        </button>
                    </div>

                    {/* Attendance Grid list */}
                    {submittingAttendance ? (
                        <div className="py-12 flex justify-center"><Loading mode="inline" message="Syncing attendance logs..." /></div>
                    ) : (
                        <div className="divide-y divide-slate-100 overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[500px]">
                                <thead>
                                    <tr className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100">
                                        <th className="p-4">Employee Name</th>
                                        <th className="p-4">Role</th>
                                        <th className="p-4">Attendance Status</th>
                                        <th className="p-4">Notes / Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {Object.keys(attendanceLogs).map((empId) => {
                                        const empInfo = employees.find(e => String(e.employee_id) === String(empId));
                                        if (!empInfo) return null;
                                        return (
                                            <tr key={empId} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="p-4 font-bold text-slate-900 text-xs">{empInfo.full_name}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                                                        empInfo.role === 'delivery_partner' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'
                                                    }`}>
                                                        {empInfo.role === 'delivery_partner' ? 'Delivery' : 'Kitchen'}
                                                    </span>
                                                </td>
                                                <td className="p-4">
                                                    <select
                                                        value={attendanceLogs[empId].status}
                                                        onChange={(e) => handleAttendanceChange(empId, 'status', e.target.value)}
                                                        className={`border rounded-xl p-2 text-xs font-bold ${
                                                            attendanceLogs[empId].status === 'present' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                                                            attendanceLogs[empId].status === 'absent_unpaid' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'
                                                        }`}
                                                    >
                                                        <option value="present">🟢 Present</option>
                                                        <option value="absent_unpaid">🔴 Absent (Unpaid)</option>
                                                        <option value="leave_paid">🟡 Leave (Paid)</option>
                                                    </select>
                                                </td>
                                                <td className="p-4">
                                                    <input
                                                        type="text"
                                                        value={attendanceLogs[empId].notes || ''}
                                                        onChange={(e) => handleAttendanceChange(empId, 'notes', e.target.value)}
                                                        placeholder="Notes (e.g. sick leave, late check-in)"
                                                        className="w-full max-w-sm bg-slate-50 border border-slate-200 rounded-xl p-2 text-xs font-bold"
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 3: SALARY CALCULATOR */}
            {activeTab === 'payroll' && (
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-6">
                    {/* Month Picker controls */}
                    <div className="flex flex-col sm:flex-row items-center justify-between border-b border-slate-100 pb-4 gap-4 bg-slate-50/50 p-4 rounded-2xl">
                        <div className="flex flex-wrap items-center gap-3">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Year</label>
                                <select
                                    value={payrollYear}
                                    onChange={(e) => setPayrollYear(parseInt(e.target.value))}
                                    className="bg-white border border-slate-200 rounded-xl p-2 px-3 text-xs font-bold"
                                >
                                    <option value="2026">2026</option>
                                    <option value="2027">2027</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Month</label>
                                <select
                                    value={payrollMonth}
                                    onChange={(e) => setPayrollMonth(parseInt(e.target.value))}
                                    className="bg-white border border-slate-200 rounded-xl p-2 px-3 text-xs font-bold"
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                        <option key={m} value={m}>
                                            {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button
                            onClick={loadPayrollCalculation}
                            className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 shadow-sm transition-all"
                        >
                            Refresh calculations
                        </button>
                    </div>

                    {/* Calculations List */}
                    {calculatingPayroll ? (
                        <div className="py-12 flex justify-center"><Loading mode="inline" message="Calculating salary slips..." /></div>
                    ) : (
                        <div className="overflow-x-auto divide-y divide-slate-100">
                            <table className="w-full text-left border-collapse min-w-[950px]">
                                <thead>
                                    <tr className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100">
                                        <th className="p-4">Employee</th>
                                        <th className="p-4">Base Pay</th>
                                        <th className="p-4">Leaves (Unpaid)</th>
                                        <th className="p-4">Deliveries (Commission)</th>
                                        <th className="p-4">Bonus (₹)</th>
                                        <th className="p-4">Total Deductions (₹)</th>
                                        <th className="p-4 font-black">Net Pay</th>
                                        <th className="p-4">Payout Transaction Detail</th>
                                        <th className="p-4 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {payrollSlips.map((slip, index) => (
                                        <tr key={slip.employee_id} className={`hover:bg-slate-50/50 transition-colors ${slip.payment_status === 'paid' ? 'bg-emerald-50/20' : ''}`}>
                                            <td className="p-4">
                                                <div className="font-bold text-slate-900 text-xs">{slip.full_name}</div>
                                                <div className="text-[10px] font-bold text-slate-400 mt-0.5">
                                                    {slip.role === 'delivery_partner' ? '🛵 Delivery' : '👨‍🍳 Kitchen'}
                                                </div>
                                            </td>
                                            <td className="p-4 font-bold text-slate-700 text-xs">₹{slip.base_salary}</td>
                                            <td className="p-4">
                                                <div className="text-xs font-bold text-red-600">{slip.unpaid_leaves} days</div>
                                                <div className="text-[10px] text-slate-400">-{slip.standard_deductions} base</div>
                                            </td>
                                            <td className="p-4">
                                                {slip.role === 'delivery_partner' ? (
                                                    <>
                                                        <div className="text-xs font-bold text-slate-800">{slip.total_deliveries} orders</div>
                                                        <div className="text-[10px] text-emerald-600 font-bold">+{slip.total_commission} comm</div>
                                                    </>
                                                ) : (
                                                    <span className="text-slate-300 text-xs font-bold">-</span>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <input
                                                    type="number"
                                                    value={slip.bonus}
                                                    disabled={slip.payment_status === 'paid'}
                                                    onChange={(e) => handlePayrollSlipChange(index, 'bonus', e.target.value)}
                                                    className="w-16 bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-xs font-bold text-emerald-600"
                                                    min="0"
                                                />
                                            </td>
                                            <td className="p-4">
                                                <input
                                                    type="number"
                                                    value={slip.deductions}
                                                    disabled={slip.payment_status === 'paid'}
                                                    onChange={(e) => handlePayrollSlipChange(index, 'deductions', e.target.value)}
                                                    className="w-16 bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-xs font-bold text-red-600"
                                                    min="0"
                                                />
                                            </td>
                                            <td className="p-4 font-black text-slate-900 text-xs">
                                                ₹{slip.net_salary}
                                            </td>
                                            <td className="p-4">
                                                {slip.payment_status === 'paid' ? (
                                                    <div className="text-[10px] font-bold text-emerald-700">
                                                        <div>Paid via Bank Transfer</div>
                                                        <div className="font-mono text-slate-400 mt-0.5">{slip.payment_reference || 'No Reference'}</div>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1">
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase">Bank Account Number:</div>
                                                        <div className="text-[10px] font-mono text-slate-700">{slip.account_number ? `${slip.bank_name} - ${slip.account_number}` : 'None Set'}</div>
                                                        <input
                                                            type="text"
                                                            placeholder="Transaction Ref"
                                                            value={slip.payment_reference || ''}
                                                            onChange={(e) => handlePayrollTextChange(index, 'payment_reference', e.target.value)}
                                                            className="w-28 bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-[10px] font-bold font-mono"
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4 text-right">
                                                {slip.payment_status === 'paid' ? (
                                                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                                                        <CheckCircle className="w-3.5 h-3.5" /> Disbursed
                                                    </span>
                                                ) : (
                                                    <div className="flex justify-end gap-1.5">
                                                        <button
                                                            onClick={() => handleSavePayrollSlip(index, 'unpaid')}
                                                            disabled={submittingSlipId !== null}
                                                            className="bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold p-2 px-3 rounded-lg text-xs"
                                                            title="Save as Draft slip"
                                                        >
                                                            Draft
                                                        </button>
                                                        <button
                                                            onClick={() => handleSavePayrollSlip(index, 'paid')}
                                                            disabled={submittingSlipId !== null || !slip.account_number}
                                                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold p-2 px-3 rounded-lg text-xs disabled:opacity-50"
                                                            title="Transfer is complete, log as Paid"
                                                        >
                                                            Disburse
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
